import SchoolIcon from "@mui/icons-material/School";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import {
  Create,
  CreateButton,
  Datagrid,
  Edit,
  FunctionField,
  List,
  SearchInput,
  SelectInput,
  SimpleForm,
  TextField,
  TopToolbar,
  usePermissions,
  type CreateProps,
  type EditProps,
  type ListProps,
} from "react-admin";
import CustomEmpty from "@/components/custom-empty";
import { DisplayDateField } from "@/components/DisplayDateField";
import { hasPermission } from "@/utils/permission-utils";
import { todayLocalYmd } from "../shared/api-error";
import { MADRASA_STUDENTS_CREATE } from "../shared/madrasa-permissions";
import { RecordTitle } from "../shared/RecordTitle";
import { StudentFormFields } from "./StudentFormFields";
import { StudentImportButton } from "./import/StudentImportButton";
import { toStudentCreatePayload, validateStudentForm } from "./student-form-utils";

type StudentRecord = {
  status?: string;
  primaryGuardian?: number;
  guardian1Name?: string;
  guardian1Phone?: string;
  guardian2Name?: string | null;
  guardian2Phone?: string | null;
};

const listFilters = [
  <SearchInput
    key="q"
    source="q"
    placeholder="Name, ITS, admission no, guardian, phone"
    alwaysOn
  />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: "ACTIVE", name: "Active" },
      { id: "INACTIVE", name: "Inactive" },
    ]}
    alwaysOn
  />,
];

const ListActions = () => {
  const { permissions } = usePermissions();
  return (
    <TopToolbar>
      <StudentImportButton />
      {hasPermission(permissions, MADRASA_STUDENTS_CREATE) && <CreateButton />}
    </TopToolbar>
  );
};

export function MadrasaStudentsList(props: ListProps) {
  return (
    <List
      {...props}
      sort={{ field: "name", order: "ASC" }}
      perPage={50}
      filters={listFilters}
      actions={<ListActions />}
      empty={
        <CustomEmpty
          title="No students yet"
          subtitle="Add your first student to start tracking enrollments and fees."
          action={
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              <StudentImportButton />
              <CreateButton variant="contained" />
            </Box>
          }
        />
      }
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="admissionNo" label="Adm. no" />
        <TextField source="name" label="Name" />
        <TextField source="itsId" label="ITS ID" />
        <FunctionField
          label="Status"
          render={(record: StudentRecord) =>
            record.status === "ACTIVE" ? (
              <Chip size="small" color="success" label="Active" />
            ) : (
              <Chip size="small" label="Inactive" />
            )
          }
        />
        <FunctionField
          label="Contact (primary guardian)"
          render={(record: StudentRecord) => {
            const useSecond = record.primaryGuardian === 2 && record.guardian2Name;
            const name = useSecond ? record.guardian2Name : record.guardian1Name;
            const phone = useSecond ? record.guardian2Phone : record.guardian1Phone;
            return `${name ?? "—"} · ${phone ?? "—"}`;
          }}
        />
        <DisplayDateField source="joinedOn" label="Joined" />
      </Datagrid>
    </List>
  );
}

export function MadrasaStudentsCreate(props: CreateProps) {
  return (
    <Create {...props} redirect="list" title="Add student" transform={toStudentCreatePayload}>
      <SimpleForm
        warnWhenUnsavedChanges
        sx={{ maxWidth: 560 }}
        validate={validateStudentForm}
        defaultValues={() => ({
          joinedOn: todayLocalYmd(),
          primaryGuardian: 1,
          admissionMode: "AUTO",
        })}
      >
        <StudentFormFields isEdit={false} />
      </SimpleForm>
    </Create>
  );
}

export function MadrasaStudentsEdit(props: EditProps) {
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      title={<RecordTitle prefix="Edit student" sources={["name"]} />}
    >
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 560 }} validate={validateStudentForm}>
        <StudentFormFields isEdit />
      </SimpleForm>
    </Edit>
  );
}

export default {
  list: MadrasaStudentsList,
  create: MadrasaStudentsCreate,
  edit: MadrasaStudentsEdit,
  icon: SchoolIcon,
  label: "Students",
  options: { label: "Students" },
  name: "madrasaStudents",
};
